// MANAGED-POLICY: the operator's queue of requests for agents (`GET /v1/operator/agent-requests`,
// open ones across every company the operator holds a grant on). Each row opens the company's
// Digital employees (where the operator adds the DE or AG the usual way) and resolves the
// request in a modal: Fulfilled or Declined, with a note the requester reads.
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
  Textarea,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../api/client";
import { agentRequestKeys } from "../agents/agent-requests";
import {
  HELPER_STAGE,
  type HelperStage,
  HelperStageBadge,
  quoteText,
} from "../agents/helper-requests";
import { problemText } from "../de/de-status";
import { formatDate } from "../format";

type QueueRow = Awaited<
  ReturnType<Api["client"]["operator"]["agentRequests"]["list"]>
>["data"][number];

const KIND_LABEL = { de: "DE", ag: "AG", helper: "Helper" } as const;

/** The stages an operator may move a helper request to from each stage (the API's rule). */
const NEXT_STAGES: Record<HelperStage, readonly HelperStage[]> = {
  requested: ["quoted", "declined"],
  quoted: ["quoted", "declined"],
  approved: ["paid", "declined"],
  paid: ["building", "declined"],
  building: ["added", "declined"],
  added: [],
  declined: [],
};

export const operatorAgentRequestsQuery = (api: Api) => ({
  queryKey: agentRequestKeys.operator(),
  queryFn: () => api.client.operator.agentRequests.list({}),
});

export function OperatorAgentRequests({ api }: { api: Api }) {
  const queue = useQuery(operatorAgentRequestsQuery(api));
  const [resolving, setResolving] = useState<QueueRow | null>(null);
  const [moving, setMoving] = useState<QueueRow | null>(null);
  const rows = queue.data?.data ?? [];
  if (queue.isError)
    return (
      <section
        className="flex items-center gap-2 text-sm text-destructive"
        data-slot="operator-agent-requests"
      >
        Requests for agents did not load.
        <Button size="xs" variant="secondary" onClick={() => void queue.refetch()}>
          Retry
        </Button>
      </section>
    );
  const partial = queue.data?.complete === false;
  if (!queue.isSuccess || (rows.length === 0 && !partial)) return null;
  return (
    <section className="flex flex-col gap-2" data-slot="operator-agent-requests">
      <h2 className="flex items-center gap-1 text-sm font-medium">
        Requests for agents ({rows.length})
        <InfoTip label="About requests">
          Managed companies ask here instead of adding agents. Add the DE or agent in the company,
          then mark the request fulfilled; the requester hears back in their inbox.
        </InfoTip>
        {partial ? (
          <InfoTip label="Some companies not read">
            Some companies could not be read: {queue.data.incomplete_reasons.join("; ")}
          </InfoTip>
        ) : null}
      </h2>
      <ul className="divide-y divide-border rounded-lg border border-border bg-card">
        {rows.map((r) => (
          <li
            key={r.request.id}
            className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm"
          >
            <Link
              to="/c/$slug/agents"
              params={{ slug: r.company.slug }}
              className="font-medium underline-offset-4 hover:underline"
            >
              {r.company.name}
            </Link>
            <span>{r.request.role}</span>
            <span className="text-muted-foreground">
              {KIND_LABEL[r.request.kind]} · {r.request.requested_by_name} ·{" "}
              {formatDate(r.request.created_at.slice(0, 10))}
            </span>
            {r.request.note ? <InfoTip label="Request note">{r.request.note}</InfoTip> : null}
            {r.request.stage ? (
              // HELPERS: a paid helper request moves through its stages instead of Resolve.
              <span className="ml-auto flex items-center gap-2">
                {r.request.quote ? (
                  <span className="text-xs tabular-nums">{quoteText(r.request.quote)}</span>
                ) : null}
                <HelperStageBadge stage={r.request.stage} />
                <Button size="xs" variant="secondary" onClick={() => setMoving(r)}>
                  Move on
                </Button>
              </span>
            ) : (
              <Button
                size="xs"
                variant="secondary"
                className="ml-auto"
                onClick={() => setResolving(r)}
              >
                Resolve
              </Button>
            )}
          </li>
        ))}
      </ul>
      {resolving ? (
        <ResolveRequestDialog api={api} row={resolving} onClose={() => setResolving(null)} />
      ) : null}
      {moving ? <HelperStageDialog api={api} row={moving} onClose={() => setMoving(null)} /> : null}
    </section>
  );
}

function ResolveRequestDialog({
  api,
  row,
  onClose,
}: {
  api: Api;
  row: QueueRow;
  onClose: () => void;
}) {
  const id = useId();
  const qc = useQueryClient();
  const [outcome, setOutcome] = useState<"fulfilled" | "declined">("fulfilled");
  const [note, setNote] = useState("");
  const resolve = useMutation({
    mutationFn: () =>
      api.client.agentRequests.resolve({
        params: { co: row.company.id, id: row.request.id },
        body: { outcome, ...(note.trim() ? { note: note.trim() } : {}) },
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: agentRequestKeys.operator() });
      await qc.invalidateQueries({ queryKey: agentRequestKeys.list(row.company.id) });
      toast.add({ title: `Request ${outcome}`, type: "success" });
      onClose();
    },
    onError: (e) =>
      toast.add({ title: problemText(e, "The request was not resolved."), type: "error" }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    resolve.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-slot="resolve-request-dialog">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Resolve request</DialogTitle>
            <DialogDescription>
              {row.company.name} · {row.request.role}
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={`${id}-outcome`}>Outcome</FieldLabel>
            <NativeSelect
              id={`${id}-outcome`}
              value={outcome}
              onChange={(e) => setOutcome(e.target.value as "fulfilled" | "declined")}
            >
              <NativeSelectOption value="fulfilled">Fulfilled</NativeSelectOption>
              <NativeSelectOption value="declined">Declined</NativeSelectOption>
            </NativeSelect>
          </Field>
          <Field>
            <FieldLabel htmlFor={`${id}-note`}>Note for the requester</FieldLabel>
            <Textarea
              id={`${id}-note`}
              rows={3}
              maxLength={2000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Field>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={resolve.isPending}>
              {resolve.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * HELPERS: move a paid helper request on — Quote (a price the client approves), Paid, Building,
 * Added or Declined, with a note. The billing side (wp/SKILLSMITH-OPPORTUNITIES) drives the same
 * route; this is the operator's manual way.
 */
function HelperStageDialog({
  api,
  row,
  onClose,
}: {
  api: Api;
  row: QueueRow;
  onClose: () => void;
}) {
  const id = useId();
  const qc = useQueryClient();
  const from = row.request.stage ?? "requested";
  const options = NEXT_STAGES[from];
  const [to, setTo] = useState<HelperStage>(options[0] ?? "declined");
  const [amount, setAmount] = useState(
    row.request.quote ? String(row.request.quote.amount_minor / 100) : "",
  );
  const [currency, setCurrency] = useState(row.request.quote?.currency ?? "USD");
  const [interval, setBilled] = useState<"one_time" | "month">(
    row.request.quote?.interval ?? "month",
  );
  const [note, setNote] = useState("");
  const minor = Math.round(Number(amount) * 100);
  const quoteOk = to !== "quoted" || (amount.trim() !== "" && Number.isFinite(minor) && minor >= 0);
  const move = useMutation({
    mutationFn: () =>
      api.client.agentRequests.stage({
        params: { co: row.company.id, id: row.request.id },
        body: {
          stage: to as "quoted" | "paid" | "building" | "added" | "declined",
          ...(to === "quoted"
            ? { quote: { amount_minor: minor, currency: currency.toUpperCase(), interval } }
            : {}),
          ...(note.trim() ? { note: note.trim() } : {}),
        },
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: agentRequestKeys.operator() });
      await qc.invalidateQueries({ queryKey: agentRequestKeys.list(row.company.id) });
      toast.add({ title: `Request: ${HELPER_STAGE[to].label}`, type: "success" });
      onClose();
    },
    onError: (e) =>
      toast.add({ title: problemText(e, "The request was not moved on."), type: "error" }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!quoteOk) return;
    move.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-slot="helper-stage-dialog">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Move helper request on</DialogTitle>
            <DialogDescription>
              {row.company.name} · {row.request.role} · {HELPER_STAGE[from].label}
            </DialogDescription>
          </DialogHeader>
          {from === "quoted" ? (
            <p className="m-0 text-xs text-muted-foreground">
              Waiting for the client to approve. You may send a new quote or decline.
            </p>
          ) : null}
          <Field>
            <FieldLabel htmlFor={`${id}-to`}>Next</FieldLabel>
            <NativeSelect
              id={`${id}-to`}
              value={to}
              onChange={(e) => setTo(e.target.value as HelperStage)}
            >
              {options.map((s) => (
                <NativeSelectOption key={s} value={s}>
                  {s === "quoted" ? "Send quote" : HELPER_STAGE[s].label}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          {to === "quoted" ? (
            <div className="grid grid-cols-3 gap-2">
              <Field>
                <FieldLabel htmlFor={`${id}-amount`}>Price</FieldLabel>
                <Input
                  id={`${id}-amount`}
                  inputMode="decimal"
                  value={amount}
                  placeholder="99"
                  onChange={(e) => setAmount(e.target.value)}
                  required
                />
              </Field>
              <Field>
                <FieldLabel htmlFor={`${id}-currency`}>Currency</FieldLabel>
                <Input
                  id={`${id}-currency`}
                  value={currency}
                  maxLength={3}
                  onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                  required
                />
              </Field>
              <Field>
                <FieldLabel htmlFor={`${id}-interval`}>Billed</FieldLabel>
                <NativeSelect
                  id={`${id}-interval`}
                  value={interval}
                  onChange={(e) => setBilled(e.target.value as "one_time" | "month")}
                >
                  <NativeSelectOption value="month">Monthly</NativeSelectOption>
                  <NativeSelectOption value="one_time">Once</NativeSelectOption>
                </NativeSelect>
              </Field>
            </div>
          ) : null}
          <Field>
            <FieldLabel htmlFor={`${id}-note`}>Note for the requester</FieldLabel>
            <Textarea
              id={`${id}-note`}
              rows={2}
              maxLength={2000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Field>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!quoteOk || move.isPending || !options.length}>
              {move.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
